/**
 * Client-side download plumbing for the v2 detail page — ports v1's
 * handlers (report v1:5623-5715, verification PDFs v1:5580-5620, ZIP
 * v1:6163-6314) onto the typed DTO. Endpoints per data-map §8. The ZIP
 * path SKIPS credit fetches via DownloadAvailability flags (pre-screen:
 * false by construction); the family variant needs v1's combined view and
 * doesn't exist on this single-applicant page. Failures surface via
 * toast.error (house toast API; v1 used alert()).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import type {
	ApplicationSummary,
	CreditSummary,
	DownloadAvailability,
	VeriffDocumentRef,
} from '#app/utils/application-detail-v2/types'
import { type HouseholdMemberRef, planHouseholdZip } from './household-zip-plan'

export type DownloadKind =
	| 'report'
	| 'zip'
	| 'income'
	| 'rent'
	| 'idDocuments'
	| 'credit'
	| 'family'
	| 'household'
	| 'etl'

const MIME_EXT: Record<string, string> = {
	'image/jpeg': '.jpeg',
	'image/jpg': '.jpg',
	'image/png': '.png',
	'image/webp': '.webp',
	'application/pdf': '.pdf',
}

const mimeToExt = (mime: string): string =>
	MIME_EXT[mime] ?? (mime ? `.${mime.split('/').pop()}` : '')

const safeName = (name: string): string =>
	(name || 'applicant').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()

const dateStamp = (): string => new Date().toISOString().split('T')[0]

function saveBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

/** GET that surfaces the route's JSON error message on failure. */
async function fetchBlobOrThrow(url: string): Promise<Blob> {
	const res = await fetch(url, { method: 'GET', credentials: 'include' })
	if (!res.ok) {
		const errBody = (await res
			.json()
			.catch(() => ({ error: res.statusText }))) as { error?: string }
		throw new Error(errBody.error || `Download failed (${res.status})`)
	}
	return res.blob()
}

async function fetchBlobOrNull(url: string): Promise<Blob | null> {
	try {
		const res = await fetch(url, { method: 'GET', credentials: 'include' })
		return res.ok ? await res.blob() : null
	} catch {
		return null
	}
}

/** Cross-origin blobs use the app proxy; same-origin demo assets load direct. */
const fetchFileBlob = (url: string): Promise<Blob | null> =>
	fetchBlobOrNull(
		url.startsWith('/')
			? url
			: `/resources/proxy-blob?url=${encodeURIComponent(url)}`,
	)

/** v1 ZIP includes only these Veriff documents (v1:6195-6200). */
const ZIP_VERIFF_NAMES = new Set([
	'face-pre',
	'document-front',
	'document-back',
])

export type { HouseholdMemberRef } from './household-zip-plan'

export interface UseDownloadsInput {
	application: ApplicationSummary
	downloads: DownloadAvailability
	credit: CreditSummary | null
	veriffDocuments: VeriffDocumentRef[]
	/** Combined page only: the household members the "Download All Files" ZIP
	 * iterates. Omitted on the single-applicant page (downloadHousehold then
	 * packages just the family report, if any). */
	householdMembers?: HouseholdMemberRef[]
}

export function useDownloads({
	application,
	downloads,
	credit,
	veriffDocuments,
	householdMembers = [],
}: UseDownloadsInput) {
	const [busy, setBusy] = useState<Partial<Record<DownloadKind, boolean>>>({})
	const propertyId = application.propertyId ?? ''
	const applicant = safeName(application.applicantName)

	const run = (kind: DownloadKind, task: () => Promise<void>) => {
		if (busy[kind]) return
		setBusy((prev) => ({ ...prev, [kind]: true }))
		void task()
			.catch((error: unknown) => {
				toast.error(error instanceof Error ? error.message : 'Download failed')
			})
			.finally(() => setBusy((prev) => ({ ...prev, [kind]: false })))
	}

	const reportUrl = (reportType: string) => {
		const params = new URLSearchParams({
			reportType,
			applicationId: application.id,
			propertyId,
		})
		// v2 renders every ported report type with the browser-free react-pdf
		// engine (individual / family / credit-report). A pre-uploaded bureau
		// credit blob still streams as-is — this only affects the generated
		// credit fallback (credit.reportData with no blob).
		params.set('engine', 'react-pdf')
		return `/resources/generate-report-pdf?${params}`
	}

	const verificationUrl = (type: 'income' | 'rent', categories?: string[]) => {
		const params = new URLSearchParams({
			applicationId: application.id,
			propertyId,
			type,
			engine: 'react-pdf',
		})
		if (categories && categories.length > 0) {
			params.set('categories', categories.join(','))
		}
		return `/resources/download-verification-pdf?${params}`
	}

	/** Credit blob chain (v1 fetchCreditReportBlob): pre-generated blob
	 * first, generated PDF fallback — gated by the availability flag. */
	const fetchCreditBlob = async (): Promise<Blob | null> => {
		if (!downloads.creditReport || !credit) return null
		if (credit.blobPath) {
			const blob = await fetchBlobOrNull(
				`/resources/download-credit-report-blob?${new URLSearchParams({
					applicationId: application.id,
					propertyId,
				})}`,
			)
			if (blob) return blob
		}
		if (credit.reportData) {
			return fetchBlobOrNull(reportUrl('credit-report'))
		}
		return null
	}

	/** "Download ETL File" (client 2026-09-09, Figma): the viewed
	 * application's ETL sheet as its own menu item again — it also stays
	 * inside the All Files ZIP. Same route + params the ZIP uses; the file
	 * name mirrors the ZIP entry's `.xls`. */
	const downloadEtl = () =>
		run('etl', async () => {
			const blob = await fetchBlobOrThrow(
				`/resources/download-etl-sheet?${new URLSearchParams({
					applicationId: application.id,
					propertyId,
				})}`,
			)
			saveBlob(blob, `etl-sheet-${applicant}-${dateStamp()}.xls`)
		})

	const downloadReport = () =>
		run('report', async () => {
			const blob = await fetchBlobOrThrow(reportUrl('individual'))
			saveBlob(blob, `individual-report-${applicant}-${dateStamp()}.pdf`)
		})

	/** Combined page (COMBINED-V2-DATA-MAP §2): the family PDF rides the
	 * existing generate-report-pdf family branch (groupId-keyed; pre-screen
	 * groups 404 there by the Task 6 guard). */
	const downloadFamilyReport = () =>
		run('family', async () => {
			const blob = await fetchBlobOrThrow(
				`/resources/generate-report-pdf?${new URLSearchParams({
					reportType: 'family',
					applicationId: application.id,
					propertyId,
					...(application.groupId ? { groupId: application.groupId } : {}),
					engine: 'react-pdf',
				})}`,
			)
			saveBlob(blob, `family-report-${applicant}-${dateStamp()}.pdf`)
		})

	/**
	 * Combined page "Download All Files" (v1 handleDownloadAllZip combined
	 * branch, v1:6286-6314): the family report at the ZIP root, then one
	 * folder per member (sorted primary → co-applicant → guarantor) holding
	 * that member's individual report + income/rent verification PDFs. Every
	 * fetch is best-effort — a member missing an artifact is simply skipped,
	 * exactly as v1 did. Per-member credit/Veriff/uploaded docs are not yet on
	 * the combined DTO, so they are omitted here (recorded follow-up).
	 */
	const downloadHousehold = () =>
		run('household', async () => {
			const plan = planHouseholdZip({
				members: householdMembers,
				entryApplicationId: application.id,
				groupId: application.groupId,
				propertyId,
				includeFamilyReport: downloads.familyReport,
			})

			const { default: JSZip } = await import('jszip')
			const zip = new JSZip()
			// JSZip reads a `/` in the name as a folder, so the plan's nested
			// paths (`member/Individual-Report.pdf`) need no explicit folders.
			for (const entry of plan) {
				const blob = await fetchBlobOrNull(entry.url)
				if (blob) zip.file(entry.path, blob)
			}

			saveBlob(
				await zip.generateAsync({ type: 'blob' }),
				`family-application-package-${dateStamp()}.zip`,
			)
		})

	const downloadVerificationPdf = (
		type: 'income' | 'rent',
		categories?: string[],
	) =>
		run(type, async () => {
			const blob = await fetchBlobOrThrow(verificationUrl(type, categories))
			saveBlob(blob, `${type}-verification-${applicant}-${dateStamp()}.pdf`)
		})

	/** Full-screening credit tile (v1's blob-first chain, v1:5631-5673);
	 * pre-screening never calls this — the flag is false by construction. */
	const downloadCreditReport = () =>
		run('credit', async () => {
			const blob = await fetchCreditBlob()
			if (!blob) throw new Error('Credit report is not available')
			saveBlob(blob, `credit-report-${dateStamp()}.pdf`)
		})

	const downloadIdDocuments = () =>
		run('idDocuments', async () => {
			const docs = veriffDocuments.filter((doc) =>
				ZIP_VERIFF_NAMES.has(doc.name),
			)
			if (docs.length === 0) throw new Error('No documents available')
			const { default: JSZip } = await import('jszip')
			const zip = new JSZip()
			for (const doc of docs) {
				const blob = await fetchFileBlob(doc.url)
				if (blob) {
					zip.file(
						`${doc.name.replace(/\s+/g, '-')}${mimeToExt(doc.mimetype)}`,
						blob,
					)
				}
			}
			saveBlob(
				await zip.generateAsync({ type: 'blob' }),
				`${application.applicantName.replace(/\s+/g, '-')}-id-documents.zip`,
			)
		})

	const downloadAllFiles = () =>
		run('zip', async () => {
			const { default: JSZip } = await import('jszip')
			const zip = new JSZip()

			const individualBlob = await fetchBlobOrNull(reportUrl('individual'))
			if (individualBlob) zip.file('Individual-Report.pdf', individualBlob)

			// The applicant's full submitted application (property selection,
			// rental history, employment) — browser-free react-pdf like the rest.
			const applicantInfoBlob = await fetchBlobOrNull(
				reportUrl('applicant-info'),
			)
			if (applicantInfoBlob)
				zip.file('Applicant-Information.pdf', applicantInfoBlob)

			const creditBlob = await fetchCreditBlob()
			if (creditBlob) zip.file('Credit-Report.pdf', creditBlob)

			if (downloads.incomeVerification) {
				const blob = await fetchBlobOrNull(verificationUrl('income'))
				if (blob) zip.file('Income-Verification.pdf', blob)
			}
			if (downloads.rentVerification) {
				const blob = await fetchBlobOrNull(verificationUrl('rent'))
				if (blob) zip.file('Rent-Verification.pdf', blob)
			}

			// ETL sheet (best-effort) — folded into the package instead of a
			// separate menu item, and always attempted (no longer staging-only).
			const etlBlob = await fetchBlobOrNull(
				`/resources/download-etl-sheet?${new URLSearchParams({
					applicationId: application.id,
					propertyId,
				})}`,
			)
			if (etlBlob) zip.file('ETL-Sheet.xls', etlBlob)

			const veriffDocs = veriffDocuments.filter((doc) =>
				ZIP_VERIFF_NAMES.has(doc.name),
			)
			if (veriffDocs.length > 0) {
				const folder = zip.folder('Verification-Documents')
				for (const doc of veriffDocs) {
					const blob = await fetchFileBlob(doc.url)
					if (blob && folder) {
						folder.file(
							`${doc.name.replace(/\s+/g, '-')}${mimeToExt(doc.mimetype)}`,
							blob,
						)
					}
				}
			}

			const zipName = `${(application.applicantName || 'applicant')
				.replace(/[^a-zA-Z0-9\s]/g, '')
				.replace(/\s+/g, '-')}-Complete-Package-${dateStamp()}.zip`
			saveBlob(await zip.generateAsync({ type: 'blob' }), zipName)
		})

	return {
		busy,
		downloadReport,
		downloadFamilyReport,
		downloadHousehold,
		downloadAllFiles,
		downloadVerificationPdf,
		downloadIdDocuments,
		downloadCreditReport,
		downloadEtl,
	}
}

export type DownloadsApi = ReturnType<typeof useDownloads>
